import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useUserSearch } from '../hooks/useUserSearch'
import { notificationRoleLabel } from '../i18n/notification_roles'
import type { UserSearchResult } from '../hooks/useUserSearch'
import { formatPhoneDisplay } from '@/utils/phone'

export interface UserChip {
  id: string
  full_name: string
  role: string
}

interface Props {
  chips: UserChip[]
  onAdd: (chip: UserChip) => void
  onRemove: (id: string) => void
}

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

export function UserSearchInput({ chips, onAdd, onRemove }: Props) {
  const { t } = useTranslation()
  const [inputValue, setInputValue] = useState('')
  const [inputFocused, setInputFocused] = useState(false)
  const debouncedQuery = useDebounce(inputValue, 250)
  const containerRef = useRef<HTMLDivElement>(null)

  const { data: results = [], isFetching } = useUserSearch(debouncedQuery)

  const chipIds = new Set(chips.map((c) => c.id))
  const filtered = results.filter((r) => !chipIds.has(r.id))

  const dropdownOpen = inputFocused && debouncedQuery.trim().length >= 2 && (filtered.length > 0 || isFetching)

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setInputFocused(false)
      }
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  function handleSelect(user: UserSearchResult) {
    onAdd({ id: user.id, full_name: user.full_name, role: user.role })
    setInputValue('')
    setInputFocused(false)
  }

  return (
    <div ref={containerRef} className="space-y-2">
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {chips.map((chip) => (
            <span
              key={chip.id}
              className="flex items-center gap-1 rounded-full border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface-raised)] px-2.5 py-0.5 text-sm text-[color:var(--stryzh-color-text-primary)]"
            >
              {chip.full_name}
              <span className="text-xs text-[color:var(--stryzh-color-text-secondary)]">
                ({notificationRoleLabel(chip.role)})
              </span>
              <button
                type="button"
                onClick={() => onRemove(chip.id)}
                className="ml-0.5 text-[color:var(--stryzh-color-text-secondary)] hover:text-[color:var(--stryzh-color-text-primary)]"
                aria-label={t('notif.userSearch.remove', { name: chip.full_name })}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="relative">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onFocus={() => setInputFocused(true)}
          onBlur={() => setInputFocused(false)}
          placeholder={t('notif.userSearch.placeholder')}
          className="w-full rounded-[var(--stryzh-radius-lg)] border border-[color:var(--stryzh-color-border-default)] bg-transparent px-3 py-2 text-sm text-[color:var(--stryzh-color-text-primary)] placeholder:text-[color:var(--stryzh-color-text-secondary)] outline-none transition-colors focus:border-[var(--stryzh-color-accent-orange)]"
        />
        {isFetching && debouncedQuery.trim().length >= 2 && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[color:var(--stryzh-color-text-secondary)]">
            …
          </span>
        )}

        {dropdownOpen && filtered.length > 0 && (
          <ul className="absolute z-20 mt-1 w-full rounded-lg border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)] py-1 shadow-lg">
            {filtered.map((user) => (
              <li key={user.id}>
                <button
                  type="button"
                  className="flex w-full flex-col px-3 py-2 text-left hover:bg-[color:var(--stryzh-color-border-subtle)]"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(user)}
                >
                  <span className="text-sm font-medium text-[color:var(--stryzh-color-text-primary)]">
                    {user.full_name}
                  </span>
                  <span className="text-xs text-[color:var(--stryzh-color-text-secondary)]">
                    {notificationRoleLabel(user.role)} · {formatPhoneDisplay(user.phone)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
